import * as React from 'react';
import clsx from 'clsx';
import { useGoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/hooks';
import { useToast } from '@/context/ToastContext';
import { useAppleLogin } from '@/hooks/useAppleLogin';
import googleIcon from '@/assets/images/svg/google_icon.svg';
import appleIcon from '@/assets/images/svg/apple_icon.svg';
import { isAppleAuthConfigured, isGoogleAuthConfigured } from '@/utils/socialAuthConfig';

/** Gradient border style shared by both OAuth buttons. */
const BUTTON_BORDER_STYLE: React.CSSProperties = {
  border: '2px solid transparent',
  backgroundClip: 'padding-box, border-box',
  backgroundOrigin: 'padding-box, border-box',
  backgroundImage: `
    linear-gradient(#FFFFFF, #FFFFFF),
    linear-gradient(90deg, #BFB0F8 0%, #E3DBFF 100%)
  `,
};

type AuthErrorSetter = React.Dispatch<React.SetStateAction<unknown>>;

interface BaseSocialButtonProps {
  buttonClasses: string;
  isLoading: boolean;
  setError: AuthErrorSetter;
}

interface GoogleSignInButtonProps extends BaseSocialButtonProps {
  googleLogin: (accessToken: string) => Promise<void>;
}

interface AppleSignInButtonProps extends BaseSocialButtonProps {
  appleLogin: Parameters<typeof useAppleLogin>[0]['onSuccess'];
}

function GoogleSignInButton({
  buttonClasses,
  googleLogin,
  isLoading,
  setError,
}: GoogleSignInButtonProps) {
  const { addToast } = useToast();
  const [googlePending, setGooglePending] = React.useState(false);

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        await googleLogin(tokenResponse.access_token);
      } catch (error) {
        setError(error);
      } finally {
        setGooglePending(false);
      }
    },
    onError: (error) => {
      setError(error);
      setGooglePending(false);
    },
    onNonOAuthError: () => {
      setGooglePending(false);
      addToast({
        title: 'Pop-up blocked',
        subtitle: 'Please allow pop-ups for this site to sign in with Google.',
        type: 'error',
      });
    },
  });

  const disabled = isLoading || googlePending;

  return (
    <button
      type='button'
      style={BUTTON_BORDER_STYLE}
      className={clsx(buttonClasses, disabled && 'cursor-not-allowed opacity-50')}
      onClick={() => {
        setGooglePending(true);
        handleGoogleLogin();
      }}
      disabled={disabled}
    >
      <img src={googleIcon} alt='' className='size-6 object-contain' />
      <span>Google</span>
    </button>
  );
}

function AppleSignInButton({
  buttonClasses,
  appleLogin,
  isLoading,
  setError,
}: AppleSignInButtonProps) {
  const handleAppleLogin = useAppleLogin({
    onSuccess: appleLogin,
    onError: setError,
  });

  return (
    <button
      type='button'
      style={BUTTON_BORDER_STYLE}
      className={clsx(buttonClasses, isLoading && 'cursor-not-allowed opacity-50')}
      onClick={handleAppleLogin}
      disabled={isLoading}
    >
      <img src={appleIcon} alt='' className='size-6 object-contain' />
      <span>Apple</span>
    </button>
  );
}

/** Google and Apple OAuth sign-in buttons. */
export const SocialButtons: React.FC = () => {
  const { googleLogin, appleLogin, isLoading } = useAuth();
  const [, setError] = React.useState<unknown>(null);

  if (!isGoogleAuthConfigured && !isAppleAuthConfigured) {
    return null;
  }

  const buttonClasses = clsx(
    'flex items-center justify-center gap-2.5',
    'rounded-[40px]',
    'font-sans font-bold text-[length:var(--vs-16,16px)] leading-32 tracking-normal text-brand-800 fold:text-20',
    'shadow-200',
    'transition-transform hover:scale-[1.02] active:scale-95',
    'h-[var(--vs-55,55px)] w-[var(--vs-168,168px)] fold:w-[185px] md:w-[202px]'
  );

  return (
    <div className='flex w-full flex-row justify-center gap-[17px] fold:gap-[24px] md:gap-[36px]'>
      {isGoogleAuthConfigured && (
        <GoogleSignInButton
          buttonClasses={buttonClasses}
          googleLogin={googleLogin}
          isLoading={isLoading}
          setError={setError}
        />
      )}
      {isAppleAuthConfigured && (
        <AppleSignInButton
          appleLogin={appleLogin}
          buttonClasses={buttonClasses}
          isLoading={isLoading}
          setError={setError}
        />
      )}
    </div>
  );
};
